import React, { useState } from 'react'
import { useGameplayContext } from '../../contexts/gameplayContext'
import { CurrencyEnum, SkillEnum, Stats } from '../../types'
import { MetricCard } from '../shared/MetricCard'
import { ChevronDown, ChevronUp, Goal, ShoppingBag, MapPin } from 'lucide-react'
import { formatMultiplier, formatUnderscoreCase } from '@/utils'
import { Tabs } from '../shared/Tabs'
import SkillShortcut from '../shared/SkillShortcut'
import CURRENCIES from '@/constants/currencies'
import { CompetitionEnum } from '@/types'
import { COMPETITIONS } from '@/constants/locations'
const formatStatName = (name: string): string => {
  return name.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())
}

const formatStatValue = (key: string, value: number): string => {
  switch (key) {
    case 'ridingWaveSecondsTotal':
      return `${Math.round(value)} seconds`
    case 'waitingForWaveMinutesTotal':
    case 'surfSessionMinutesTotal':
      return `${Math.round(value)} minutes`
    case 'damageTotal':
      return `$${value.toFixed(2)}`
    default:
      return value.toLocaleString() // Use toLocaleString for better number formatting
  }
}

const getEmojiForStat = (key: string): string => {
  switch (key) {
    case 'wavesTotal':
      return '🌊'
    case 'ridingWaveSecondsTotal':
      return '⏱️'
    case 'waitingForWaveMinutesTotal':
      return '⌛'
    case 'surfSessionsTotal':
      return '🏄‍♂️'
    case 'surfSessionMinutesTotal':
      return '⏰'
    case 'wipeoutsTotal':
      return '💥'
    case 'injuriesTotal':
      return '🤕'
    case 'damageTotal':
      return '💰'
    case 'respectTotal':
      return '👏'
    case 'criticalMovesTotal':
      return '🔥'
    case 'gameDaysTotal':
      return '📅'
    default:
      return '📊'
  }
}

const Card = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`rounded-lg shadow-md ${className}`}>{children}</div>
)

const CardContent = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-6 ${className}`}>{children}</div>
)

const StatsGrid: React.FC<{ stats: Stats }> = ({ stats }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {Object.entries(stats).map(([key, value]) => (
      <MetricCard
        key={key}
        emoji={getEmojiForStat(key)}
        label={formatStatName(key)}
        value={formatStatValue(key, value)}
      />
    ))}
  </div>
)

interface EffectCardProps {
  title: string
  totalMultiplier: number
  multipliers: Array<{
    category: string
    sourceCategory: string
    sourceName: string
    multiplier: number
  }>
  thumbnail: React.ReactNode
}

const EffectCard: React.FC<EffectCardProps> = ({ title, totalMultiplier, multipliers, thumbnail }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const getEffectStyle = (sourceCategory: string) => {
    switch (sourceCategory) {
      case 'upgrade':
        return {
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          icon: <ShoppingBag className="w-4 h-4" />
        }
      case 'surfSpot':
        return {
          bgColor: 'bg-amber-100',
          textColor: 'text-amber-800',
          icon: <MapPin className="w-4 h-4" />
        }
      default:
        return {
          bgColor: 'bg-purple-100',
          textColor: 'text-purple-800',
          icon: <Goal className="w-7 h-7" />
        }
    }
  }

  return (
    <Card className="bg-white overflow-hidden h-fit">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex-shrink-0">{thumbnail}</div>
            <span className="font-semibold">{formatUnderscoreCase(title)}</span>
            <span className={`font-semibold ${totalMultiplier < 1 ? 'text-red-600' : 'text-emerald-600'}`}>
              {formatMultiplier(totalMultiplier)}
            </span>
          </div>
          <button
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </CardContent>
      <div className={`transition-all duration-200 ${isExpanded ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-1">
            {multipliers.map((effect, index) => {
              const style = getEffectStyle(effect.sourceCategory)
              return (
                <div
                  key={index}
                  className={`px-2 py-1 rounded-full text-sm flex items-center gap-1
                    ${style.bgColor} ${style.textColor}`}
                >
                  {style.icon}
                  <span className="font-medium">{formatUnderscoreCase(effect.sourceName)}</span>
                  <span
                    className={`${
                      effect.multiplier < 1 ? 'bg-red-800 ' : 'bg-green-800'
                    } text-white text-xs font-semibold ml-1 px-1.5 py-0.5 rounded-full`}
                  >
                    {formatMultiplier(effect.multiplier)}
                  </span>
                </div>
              )
            })}
          </div>
        </CardContent>
      </div>
    </Card>
  )
}

const EffectsSection = () => {
  const { gameData } = useGameplayContext()

  const currencyEffects = Object.entries(gameData.currencies)
    .filter(([_, currency]) => currency.appliedMultipliers && currency.appliedMultipliers.length > 0)
    .map(([key, currency]) => ({
      key,
      totalMultiplier: currency.totalMultiplier || 1,
      multipliers: currency.appliedMultipliers || [],
      thumbnail: CURRENCIES[key as CurrencyEnum]?.icon || '📊'
    }))

  const skillEffects = Object.entries(gameData.skills)
    .filter(([_, skill]) => skill.appliedMultipliers && skill.appliedMultipliers.length > 0)
    .map(([key, skill]) => ({
      key,
      totalMultiplier: skill.totalMultiplier || 1,
      multipliers: skill.appliedMultipliers || [],
      thumbnail: <SkillShortcut skill={key as SkillEnum} />
    }))

  if (currencyEffects.length === 0 && skillEffects.length === 0) {
    return (
      <div className="text-gray-500 text-center py-4 bg-white rounded-lg">
        No active effects yet. Purchase upgrades or complete quests to gain multipliers!
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {currencyEffects.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-2 text-white">Currency Multipliers</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {currencyEffects.map((effect) => (
              <EffectCard
                key={effect.key}
                title={effect.key}
                totalMultiplier={effect.totalMultiplier}
                multipliers={effect.multipliers}
                thumbnail={effect.thumbnail}
              />
            ))}
          </div>
        </div>
      )}

      {skillEffects.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-2 text-white">Skill Multipliers</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {skillEffects.map((effect) => (
              <EffectCard
                key={effect.key}
                title={effect.key}
                totalMultiplier={effect.totalMultiplier}
                multipliers={effect.multipliers}
                thumbnail={effect.thumbnail}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const CompetitionStatsSection = () => {
  const { gameData } = useGameplayContext()

  if (Object.keys(gameData.competitionStats).length === 0) {
    return (
      <div className="text-gray-500 text-center py-4 bg-white rounded-lg">
        No competition history yet. Enter competitions to build your competitive record!
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Object.entries(gameData.competitionStats).map(([compKey, stats]) => {
        const competition = COMPETITIONS[compKey as CompetitionEnum]
        return (
          competition && (
            <div key={compKey} className="bg-gradient-to-br from-white to-blue-100 rounded-lg p-4 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-blue-900">🏆 {competition.name}</h3>
              </div>

              <div className="space-y-2">
                {/* Main Stats */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-blue-50 p-2 rounded">
                    <div className="text-sm text-blue-900">Entries</div>
                    <div className="text-lg font-bold text-blue-700">{stats.enrolled}</div>
                  </div>
                  <div className="bg-yellow-50 p-2 rounded">
                    <div className="text-sm text-yellow-900">Victories</div>
                    <div className="text-lg font-bold text-yellow-700">{stats.won}</div>
                  </div>
                </div>

                {/* Round Progress */}
                <div className="space-y-1">
                  <div className="text-sm font-medium text-blue-900 mb-2">Best Round Reached:</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span>Finals:</span>
                      <span className="font-medium">{stats.finals}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Semifinals:</span>
                      <span className="font-medium">{stats.semifinals}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Quarterfinals:</span>
                      <span className="font-medium">{stats.quarterfinals}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Round 4:</span>
                      <span className="font-medium">{stats.round_4}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Round 3:</span>
                      <span className="font-medium">{stats.round_3}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Round 2:</span>
                      <span className="font-medium">{stats.round_2}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Round 1:</span>
                      <span className="font-medium">{stats.round_1}</span>
                    </div>
                  </div>
                </div>

                {/* Prize Money */}
                <div className="mt-4 bg-green-50 p-2 rounded">
                  <div className="text-sm text-green-900">Total Prize Money</div>
                  <div className="text-lg font-bold text-green-700">${stats.prizeWon.toLocaleString()}</div>
                </div>
              </div>
            </div>
          )
        )
      })}
    </div>
  )
}

export const StatisticsScreen: React.FC = () => {
  const { gameData, setGameData } = useGameplayContext()
  const globalStats: Stats = gameData.stats
  const locationStats: Stats = gameData.locationStats[gameData.currentLocation]

  const handleTabChange = (value: string) => {
    setGameData((prev) => ({
      ...prev,
      tabs: {
        ...prev.tabs,
        stats: value
      }
    }))
  }

  const tabs = [
    { value: 'global', label: '🌍 Global Stats' },
    { value: 'local', label: '📍 Local Stats' },
    { value: 'effects', label: '⚡ Skill Multipliers' },
    { value: 'competitions', label: '🏆 Competitions' }
  ]

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-white drop-shadow-md">📊 Statistics</h2>

      <Tabs tabs={tabs} activeTab={gameData.tabs.stats} onChange={handleTabChange} />

      {gameData.tabs.stats === 'global' && (
        <div>
          <StatsGrid stats={globalStats} />
        </div>
      )}

      {gameData.tabs.stats === 'local' && (
        <div>
          <h3 className="text-xl font-bold text-white drop-shadow-md mb-4">
            {gameData.currentLocation.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
          </h3>
          <StatsGrid stats={locationStats} />
        </div>
      )}

      {gameData.tabs.stats === 'effects' && (
        <div>
          <EffectsSection />
        </div>
      )}

      {gameData.tabs.stats === 'competitions' && <CompetitionStatsSection />}
    </div>
  )
}
